import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { QUIZ_QUERY_KEYS } from './quizzes';
import { useImproveAxios } from '../../hooks/useImproveAxios';
import {
	getImproveAPI,
	type CreateQuestionBody,
	type CreateQuestionResponse,
	type QuestionSlimDto,
	type QuizWithQuestionsDto,
	type SearchQuestionsBody,
	type SearchQuestionsResponse,
} from '../gen';

/** Re-export Orval-generated DTOs (`improve-api/client`: `npm run swagger` then `npm run generate:local`). */
export type CreateQuizQuestionBody = CreateQuestionBody;
export type CreateQuizQuestionResponse = CreateQuestionResponse;

/**
 * Quiz builder types - using generated types from improve-api
 */
export type QuestionSlim = QuestionSlimDto;
export type QuizWithQuestions = QuizWithQuestionsDto;
export type SearchQuestionsParams = SearchQuestionsBody;
export type SearchQuestionsResult = SearchQuestionsResponse;

/**
 * Hook to fetch quiz with full question composition for admin builder
 */
export function useGetQuizWithQuestions(quizId: number | undefined) {
	const axiosConfig = useImproveAxios();
	const { quizzesControllerGetWithQuestions } = getImproveAPI();

	return useQuery<QuizWithQuestionsDto>({
		queryKey: [QUIZ_QUERY_KEYS.BUILDER, quizId],
		queryFn: async () => {
			if (!quizId) throw new Error('Quiz ID is required');
			const response = await quizzesControllerGetWithQuestions(quizId, axiosConfig);
			return response.data;
		},
		enabled: !!quizId,
		staleTime: 30 * 1000,
	});
}

/**
 * Hook to search questions for quiz builder
 */
export function useSearchQuestions() {
	const axiosConfig = useImproveAxios();
	const { quizzesControllerSearchQuestions } = getImproveAPI();

	return useMutation<SearchQuestionsResponse, Error, SearchQuestionsParams>({
		mutationFn: async (params: SearchQuestionsParams) => {
			const response = await quizzesControllerSearchQuestions(params, axiosConfig);
			return response.data;
		},
	});
}

export function useCreateQuizQuestion() {
	const axiosConfig = useImproveAxios();
	const queryClient = useQueryClient();
	const { quizzesControllerCreateQuestion } = getImproveAPI();

	return useMutation<CreateQuestionResponse, Error, CreateQuestionBody>({
		mutationFn: async (body) => {
			const response = await quizzesControllerCreateQuestion(body, axiosConfig);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QUIZ_QUERY_KEYS.LIST] });
		},
	});
}

/**
 * Hook to update quiz composition
 * Uses the existing quizzesControllerUpdate endpoint with composition field.
 */
export function useUpdateQuizComposition(quizId: number) {
	const axiosConfig = useImproveAxios();
	const queryClient = useQueryClient();
	const { quizzesControllerUpdate } = getImproveAPI();

	return useMutation<void, Error, QuestionSlimDto[]>({
		mutationFn: async (composition: QuestionSlimDto[]) => {
			await quizzesControllerUpdate(quizId, { composition } as any, axiosConfig);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QUIZ_QUERY_KEYS.DETAIL, quizId] });
			queryClient.invalidateQueries({ queryKey: [QUIZ_QUERY_KEYS.BUILDER, quizId] });
			queryClient.invalidateQueries({ queryKey: [QUIZ_QUERY_KEYS.LIST] });
		},
	});
}
